import React, { useState, useEffect } from 'react'
import { io } from 'socket.io-client'
import * as api from '../../lib/api'
import './PublicVacancyPage.css' // Reuse public layout styles

const STATUS_CONFIG = {
  new: {
    label: 'Новый',
    color: '#3B82F6', // Blue
    title: 'Отклик успешно подан!',
    desc: 'Мы получили вашу анкету. Менеджер начнет её рассмотрение в ближайшее время. Обычно это занимает от нескольких часов до одного дня. Пожалуйста, сохраните эту страницу для отслеживания.'
  },
  reviewing: {
    label: 'На рассмотрении',
    color: '#F59E0B', // Orange
    title: 'Анкета на рассмотрении',
    desc: 'Менеджер изучает вашу анкету и проверяет соответствие требованиям. Мы свяжемся с вами сразу после завершения оценки.'
  },
  interview: {
    label: 'Собеседование',
    color: '#10B981', // Greenish-blue
    title: 'Вас пригласили на собеседование! 🎉',
    desc: 'Поздравляем! Ваша анкета успешно прошла первичный отбор. В ближайшее время наш представитель свяжется с вами по указанному в анкете телефону для согласования удобного времени и формата собеседования.'
  },
  accepted: {
    label: 'Принят в команду',
    color: '#10B981', // Green
    title: 'Вы приняты! Добро пожаловать! 🥳',
    desc: 'Поздравляем с успешным прохождением отбора! Мы рады пригласить вас присоединиться к нашей команде. В ближайшее время менеджер свяжется с вами для обсуждения даты начала работы и оформления документов.'
  },
  rejected: {
    label: 'Отклонён',
    color: '#6B7280', // Grey
    title: 'Отбор завершен',
    desc: 'Благодарим вас за интерес к нашей вакансии и за заполнение анкеты. К сожалению, на текущий момент мы не готовы сделать вам предложение. Ваши контакты сохранены в нашей базе, и мы вернемся к вам, если у нас появятся подходящие предложения в будущем.'
  }
}

export default function PublicResponseStatusPage({ responseId }) {
  const [response, setResponse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.body.classList.add('public-page')
    document.documentElement.classList.add('public-page')
    return () => {
      document.body.classList.remove('public-page')
      document.documentElement.classList.remove('public-page')
    }
  }, [])

  useEffect(() => {
    async function load() {
      try {
        const data = await api.getPublicResponseStatus(responseId)
        setResponse(data)
      } catch (err) {
        setError(err.message || 'Отклик не найден')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [responseId])

  // Реальное время: WebSocket для гостей
  useEffect(() => {
    if (loading || error || !responseId) return

    const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/api$/, '') || '/'
    const socket = io(baseUrl, {
      path: '/socket.io/',
      auth: { isGuest: true },
      reconnection: true,
      transports: ['websocket', 'polling'],
    })

    socket.on('connect', () => {
      console.log('[WS-Guest] Connected, joining room for response:', responseId)
      socket.emit('join-response-status', responseId)
    })

    socket.on('status-updated', (data) => {
      console.log('[WS-Guest] Status updated in realtime:', data)
      setResponse(prev => prev ? { ...prev, status: data.status } : null)
    })

    return () => {
      console.log('[WS-Guest] Disconnecting')
      socket.disconnect()
    }
  }, [loading, error, responseId])

  if (loading) {
    return (
      <div className="public-vacancy-page">
        <div className="public-vacancy-loading">Загрузка информации...</div>
      </div>
    )
  }

  if (error || !response) {
    return (
      <div className="public-vacancy-page">
        <div className="public-vacancy-error card">
          <h2>Упс!</h2>
          <p>{error || 'Отклик не найден'}</p>
        </div>
      </div>
    )
  }

  const currentStatus = response.status || 'new'
  const config = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.new

  // Рассчитываем прогресс шагов
  const getStepState = (stepIndex) => {
    const statusOrder = { new: 0, reviewing: 1, interview: 2, accepted: 3, rejected: 3 }
    const currentOrder = statusOrder[currentStatus] ?? 0

    if (stepIndex < currentOrder) return 'completed'
    if (stepIndex === currentOrder) {
      if (currentStatus === 'rejected') return 'failed'
      if (currentStatus === 'accepted') return 'completed'
      return 'active'
    }
    return 'pending'
  }

  return (
    <div className="public-vacancy-page" style={{ background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh', padding: '40px 20px' }}>
      <div className="public-vacancy-container" style={{ width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Карточка заголовка */}
        <div className="card" style={{ padding: '32px', textAlign: 'center' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-tertiary)' }}>Статус кандидата</span>
          <h2 style={{ fontSize: '24px', fontWeight: 800, marginTop: '8px', color: 'var(--text-primary)' }}>{response.full_name}</h2>
          <p style={{ fontSize: '15px', color: 'var(--text-secondary)', marginTop: '6px' }}>Отклик на вакансию: <strong>{response.vacancy_title}</strong></p>
          {response.pvz_name && <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Пункт выдачи: <strong>{response.pvz_name}</strong></p>}
        </div>

        {/* Интерактивный Степпер (Таймлайн) */}
        <div className="card" style={{ padding: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', marginBottom: '24px' }}>
            
            {/* Соединительная полоса прогресса */}
            <div style={{
              position: 'absolute',
              top: '18px',
              left: '30px',
              right: '30px',
              height: '3px',
              background: 'var(--border-color)',
              zIndex: 1
            }} />

            {/* Заполненная полоса прогресса */}
            {(() => {
              const statusOrder = { new: 0, reviewing: 1, interview: 2, accepted: 3, rejected: 3 }
              const currentOrder = statusOrder[currentStatus] ?? 0
              const progressWidth = currentOrder === 0 ? '0%' : currentOrder === 1 ? '33%' : currentOrder === 2 ? '66%' : '100%'
              const progressBg = currentStatus === 'rejected' ? 'var(--text-tertiary)' : 'var(--success)'
              return (
                <div style={{
                  position: 'absolute',
                  top: '18px',
                  left: '30px',
                  width: `calc(${progressWidth} - ${currentOrder === 3 ? '0px' : '40px'})`,
                  height: '3px',
                  background: progressBg,
                  transition: 'all 0.5s ease',
                  zIndex: 2
                }} />
              )
            })()}

            {/* Шаги */}
            {[
              { label: 'Подан', activeLabel: 'Отклик подан' },
              { label: 'Рассмотрение', activeLabel: 'На рассмотрении' },
              { label: 'Интервью', activeLabel: 'Собеседование' },
              { label: currentStatus === 'rejected' ? 'Завершено' : 'Принят', activeLabel: currentStatus === 'rejected' ? 'Отклонён' : 'Принят!' }
            ].map((step, idx) => {
              const state = getStepState(idx)
              
              let bg = 'var(--surface-hover)'
              let border = '2px solid var(--border-color)'
              let iconColor = 'var(--text-secondary)'
              let labelColor = 'var(--text-secondary)'
              let icon = idx + 1

              if (state === 'completed') {
                bg = 'var(--success)'
                border = '2px solid var(--success)'
                iconColor = '#fff'
                labelColor = 'var(--success)'
                icon = '✓'
              } else if (state === 'active') {
                bg = 'var(--wb-primary)'
                border = '2px solid var(--wb-primary)'
                iconColor = '#fff'
                labelColor = 'var(--wb-primary)'
              } else if (state === 'failed') {
                bg = 'var(--danger)'
                border = '2px solid var(--danger)'
                iconColor = '#fff'
                labelColor = 'var(--danger)'
                icon = '✕'
              }

              return (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 3, width: '70px', position: 'relative' }}>
                  <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '50%',
                    background: bg,
                    border: border,
                    color: iconColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}>
                    {icon}
                  </div>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: state !== 'pending' ? 700 : 500,
                    color: labelColor,
                    marginTop: '8px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}>
                    {state === 'active' || state === 'failed' ? step.activeLabel : step.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Карточка детального описания статуса */}
        <div className="card animate-scale-in" style={{ padding: '32px', borderLeft: `5px solid ${config.color || 'var(--wb-primary)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: config.color
            }} />
            <h3 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)' }}>{config.title}</h3>
          </div>
          <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{config.desc}</p>
        </div>

        {/* Инструкция / Кнопка назад */}
        <div style={{ textAlign: 'center', padding: '10px 0 30px 0' }}>
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            Статус обновляется автоматически в реальном времени. Обновлять страницу не требуется.
          </p>
        </div>

      </div>
    </div>
  )
}
